"use client";

import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Layout from "../../layout/Layout";
import { BackIcon } from "../../icons";
import { cn } from "../../../utils/classnames";
import { useLoading } from "../../../components/LoadingContext";
import { getBooking } from "../../../actions/admin/booking";
import CustomerInformation from "./_components/CustomerInformation";
import BookingDetails from "./_components/BookingDetails";
import TimeCostDetail from "./_components/TimeCostDetail";
import PaymentDetail from "./_components/PaymentDetail";
import AssignedVehicleDetail from "./_components/AssignedVehicleDetail";
import BookingCancellation from "./_components/BookingCancellation";
import BookingDetailSkeleton from "./_components/BookingDetailSkeleton";
import customAxios from "../../../utils/customAxios";
import { BackAltIcon } from "../../icons"
import { getEffectiveBookingStatus } from "./constants";

// Vehicle & driver assignment is rolled out per environment.
const SHOW_ASSIGNMENT = process.env.REACT_APP_DRIVER_ASSIGNMENT === "true";

const STATUS_MAP = {
  CONFIRMED: { label: "Confirmed", className: "tw:bg-[#3CAEA3]" },
  CANCELLED: { label: "Cancelled", className: "tw:bg-[#EA4335]" },
  PENDING: { label: "Pending Confirmation", className: "tw:bg-[#F6BB2A]" },
  ABANDONED: { label: "Abandoned Cart", className: "tw:!bg-[#F3F3F3] tw:!text-[#8D8D8D]" },
};

const getPaxCount = (paxList = [], type = "") =>
  Number(
    (paxList || []).find((item) => String(item?.paxtype || "").toLowerCase() === String(type).toLowerCase())?.paxcount || 0
  );

const getLuggageCount = (luggageList = [], matcher = "check") =>
  Number(
    (luggageList || []).find((item) => String(item?.luggagetype || "").toLowerCase().includes(matcher))?.luggagecount || 0
  );

const ManageBookingV2 = ({ match }) => {
  const dispatch = useDispatch();
  const bookingState = useSelector((state) => state.booking);
  const { loading, booking } = bookingState || {};
  const { showLoading, closeLoading } = useLoading();
  const bookingId = match?.params?.id;

  const [isApprovingDraft, setIsApprovingDraft] = useState(false);
  const [draftSuccessMessage, setDraftSuccessMessage] = useState("");

  useEffect(() => {
    if (!bookingId) return;
    dispatch(getBooking(bookingId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, bookingId]);

  const requestChangeList = Array.isArray(booking?.requestChangeBookings)
    ? booking.requestChangeBookings
    : Array.isArray(booking?.requestChangeBooking)
      ? booking.requestChangeBooking
      : [];
  const draft = requestChangeList.find(
    (item) => String(item?.status || "").toUpperCase() === "DRAFT"
  ) ?? null;

  const handleApproveDraft = useCallback(async () => {
    if (!draft?._id) return;
    if (!window.confirm("Approve this draft booking modification? The booking will be updated immediately.")) return;
    setIsApprovingDraft(true);
    try {
      await customAxios.post("/api/admin/request-change-booking/approve-draft", {
        requestId: draft._id,
      });
      setDraftSuccessMessage("Draft approved — booking has been updated.");
      dispatch(getBooking(bookingId));
    } catch (err) {
      console.error("Approve draft failed:", err);
    } finally {
      setIsApprovingDraft(false);
    }
  }, [draft?._id, bookingId, dispatch]);

  if (loading || !booking) {
    return (
      <Layout>
        <main className="col-md-9 ms-sm-auto col-lg-10 px-md-4 tw:pb-[42px] w-100">
          <BookingDetailSkeleton />
        </main>
      </Layout>
    );
  }

  const effectiveStatus = getEffectiveBookingStatus(booking);
  const statusMeta = booking?.isAbandoned === true ? STATUS_MAP.ABANDONED : STATUS_MAP[effectiveStatus] || STATUS_MAP.PENDING;
  const bookingInfo = Array.isArray(booking?.bookingInfo) ? booking.bookingInfo : [];
  const firstBookingInfo = bookingInfo[0] || {};
  const paxList = Array.isArray(booking?.pax) ? booking.pax : firstBookingInfo?.pax || [];
  const luggageList = Array.isArray(booking?.luggage) ? booking.luggage : firstBookingInfo?.luggage || [];

  const adultCount = getPaxCount(paxList, "adult");
  const childCount = getPaxCount(paxList, "child");
  const toddlerCount = getPaxCount(paxList, "toddler");
  const infantCount = getPaxCount(paxList, "baby") || getPaxCount(paxList, "infant");
  const checkinCount = getLuggageCount(luggageList, "check");
  const cabinCount = getLuggageCount(luggageList, "cabin");
  const specialRequestText = booking?.message?.[0]?.note || "-";
  const passengerNameText = `${booking?.passengerPronounced || ""} ${booking?.passengerName || "-"}`.trim();
  const passengerPhoneText = booking?.passengerPrefixNumber && booking?.passengerPhoneNumber
    ? `(${booking?.passengerPrefixNumber}) ${booking?.passengerPhoneNumber}`
    : "-";
  const vehiclePassengerRows = (bookingInfo.length ? bookingInfo : [firstBookingInfo]).map((item, index) => ({
    key: item?._id || item?.vehicleID?._id || `vehicle-row-${index}`,
    index,
    vehicleLabel: `Vehicle #${index + 1}`,
    vehicleName: item?.vehicleTypeName || item?.vehicleTypeID?.typename || item?.vehicleID?.vehiclename || "-",
  }));
  const isMultipleVehicle = vehiclePassengerRows.length > 1;

  return (
    <Layout>
      <main className="col-md-9 ms-sm-auto col-lg-10 px-md-4 tw:pb-[42px] w-100">
        <div className="tw:font-hanken-grotesk tw:text-[#212529]">
          <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-6 tw:pb-[28px] tw:lg:pb-[42px] tw:px-5 tw:pt-8">
            <Link
              to={!booking?.isAbandoned ? "/admin/bookinglist" : "/admin/abandentcartlist"}
              className="tw:inline-flex tw:items-center tw:gap-[10px] tw:rounded-[5px] tw:border tw:border-[#e8e8e8] tw:bg-[#e8e8e8] tw:px-3 tw:py-2 tw:text-[#282828] tw:!no-underline"
            >
              <BackIcon color="#8D8D8D" width={7} height={10} />
              <span className="tw:!font-hanken-grotesk tw:text-[#282828] tw:text-[14px]">Back</span>
            </Link>

            <div className="tw:flex tw:flex-1 tw:items-center tw:justify-between tw:gap-4">
              <div className="tw:flex tw:flex-col">
                <div className="tw:!font-hanken-grotesk tw:font-[500] tw:text-[14px] tw:text-[#7c7b7b]">
                  {`[Cart #${booking?.cartID || ""}・Booking #${booking?.key || ""}]`}
                </div>
                <div className="tw:!font-hanken-grotesk tw:text-[24px] tw:font-[500] tw:leading-normal tw:text-[#212529]">
                  Booking Details
                </div>
              </div>

              <span
                className={cn(
                  "tw:inline-flex tw:h-[45px] tw:items-center tw:justify-center tw:whitespace-nowrap tw:rounded-[5px] tw:px-6 tw:text-[15px] tw:leading-[22px] tw:text-white",
                  statusMeta.className
                )}
              >
                {statusMeta.label}
              </span>
            </div>
          </div>

          {draftSuccessMessage && (
            <div className="tw:mx-5 tw:mb-4 tw:rounded-[5px] tw:border tw:border-[#3CAEA3] tw:bg-[#F0FAFA] tw:px-5 tw:py-3">
              <span className="tw:text-[14px] tw:font-semibold tw:text-[#3CAEA3] tw:font-hanken-grotesk">{draftSuccessMessage}</span>
            </div>
          )}

          <CustomerInformation
            isMultipleVehicle={isMultipleVehicle}
            vehiclePassengerRows={vehiclePassengerRows}
            passengerNameText={passengerNameText}
            passengerPhoneText={passengerPhoneText}
            booking={booking}
          />

          <BookingDetails
            booking={booking}
            adultCount={adultCount}
            childCount={childCount}
            toddlerCount={toddlerCount}
            infantCount={infantCount}
            checkinCount={checkinCount}
            cabinCount={cabinCount}
            specialRequestText={specialRequestText}
          />

          <TimeCostDetail
            booking={booking}
          />

          {effectiveStatus === "CANCELLED" && (
            <BookingCancellation booking={booking} />
          )}

          <PaymentDetail
            booking={booking}
            showLoading={showLoading}
            closeLoading={closeLoading}
          />

          {
            SHOW_ASSIGNMENT && (booking.paymentStatus === "CHARGED" || booking.paymentStatus === "ADDED_TO_MONTHLY_TOTAL") && effectiveStatus !== "CANCELLED" && (
              <AssignedVehicleDetail
                booking={booking}
              />
            )
          }
        </div>
      </main>
    </Layout>
  );
};

export default ManageBookingV2;